import type { ItemFieldRow } from './item-field-reader';
import { readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { appendCompiledLayerInPlace } from './status-row-construction';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { createPcPreliminaryEssenceCompiler } from './preliminary-essence';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { createFormulaReadContext } from './formula-read-context';
import { constructPlayerClassificationFromBase } from './from-base-player-classification';
import { constructPlayerSaveSectionMedia, constructEncounterSourcePictures } from './from-base-player-media';
import { createAbilityFinalizerGates } from './ability-finalizer-gates';
import { answerConditionContext, ConditionContextUnknown } from './condition-context';
import { foldCreatureDefenseEntries } from './creature-defense';
import { DEFENSE_SLOTS } from './creature-defense-delta';
import type { FromBasePlayerSkillMetadata } from './from-base-player-skills';
import { constructPlayerSourceFromBase, constructPlayerEncounterSourceFromBase } from './from-base-player-source';
import {
    createFromBasePlayerContext, fromBaseClone, fromBaseRecord, fromBaseUuid,
    FromBaseInputMissing, FromBaseStageMissing,
} from './from-base-context';
import type { FromBaseRow, FromBasePlayerInput, FromBasePlayerMetadata } from './from-base-context';
import { projectDefinitionPendingCacheRead } from './definition-cache-surface';
import { playerChoiceGrant } from './player-choice-grant';
import { constructPlayerPreparedMapFromBase } from './from-base-player-display';

/** Required finalizer stages; each checkpoint removes only the stages it constructs. */
export const FROM_BASE_PLAYER_REMAINING_STAGES = [
    'classification-template-swarm-media', 'tiers-only-skills', 'status-environment-layering',
    'inventory-construction-grants', 'size-shapeshift-vitality', 'nested-grants-override-prune',
    'integer-rollup-full-skills', 'defenses-flags-ac-capacity', 'raw-status-consequence-plans',
    'ability-spell-finalization', 'post-finalizer-clusters', 'catalog-lean-normalization',
    'save-section-definition-finalization',
] as const;
export type FromBasePlayerPrefixResult = {
    kind: 'constructed-prefix';
    row: FromBaseRow;
    completedStages: readonly string[];
    missingStages: readonly string[];
} | { kind: 'missing-input'; stage: string; dependency: string; id: string }
  | { kind: 'missing-stage'; stage: string; detail: string };

/** Constructs selected grants from saved PC/choice rows and authored definitions.
 * The result is an intermediate host, never a finished computed creature/cache. */
export function constructPlayerPrefixFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerMetadata): FromBasePlayerPrefixResult {
    let activeStage = 'player-source-initialization';
    try {
        const context = createFromBasePlayerContext(base, metadata), { readers, definition } = context;
        const pc = constructPlayerSourceFromBase(base.player_characters, readers.layerValue), pcId = phpJsonString(readers.layerValue(pc, 'item_id'));
        const savedValue = (name: string): unknown => readers.layerValue(base.player_characters, name);
        const totalLevel = phpJsonInt(savedValue('total_level') ?? Array.from({ length: 20 }, (_, i) =>
            phpJsonInt(savedValue(`class_level_${i + 1}`))).reduce((sum, level) => sum + level, 0));
        pc.item_layer_types = { total_level: totalLevel };
        let host: FromBaseRow = {
            items: { item_id: pcId, item_type: 'player_character' }, player_characters: pc,
            item_layer_types: { total_level: totalLevel, weight: savedValue('weight') ?? 0,
                creature_multiclassed: phpJsonTruthy(savedValue('class_2')) ? 1 : 0 },
        };
        // Keep saved source buckets intact. Do not put the raw flat inventory envelope
        // into the compiled-tree slot before the inventory construction stage runs.
        if (base.encounter_creatures) host.encounter_creatures = constructEncounterSourcePictures(
            constructPlayerEncounterSourceFromBase(base.encounter_creatures, base.encounter_context, base.caster_summons),
            [...base.pictures ?? [], ...base.picture_sources ?? []], metadata.cdnUrl);
        Object.assign(host, constructPlayerPreparedMapFromBase(base));
        const layers = host.item_layer_types as FromBaseRow;
        const value = (row: ItemFieldRow | null, name: string): unknown => readers.layerValue(row, name);
        const entries = (row: ItemFieldRow | null, name: string): unknown[] => Object.values(readers.layerEntries(row, name));
        const required = (row: ItemFieldRow | null): number => phpJsonInt(value(row, 'requires_level') ?? value(row, 'requires_class_level') ?? 1);
        const lookup = (raw: unknown): ItemFieldRow | null => fromBaseUuid(raw) ? definition(raw) : null;
        const append = (name: string, row: unknown): void => appendCompiledLayerInPlace(host, name, row);
        const nameOf = (row: ItemFieldRow | null): unknown => readers.itemField(row, 'item_name');
        const known: Record<'spell_known' | 'curse_known', ItemFieldRow[]> = { spell_known: [], curse_known: [] };
        function saveSection(name: string, provider: ItemFieldRow | null): void {
            if (!provider) return;
            const rows = (host[name] ??= []) as ItemFieldRow[];
            rows.push(projectDefinitionPendingCacheRead(constructPlayerSaveSectionMedia(provider, metadata.cdnUrl, readers.layerValue)));
        }
        function foldKnown(provider: ItemFieldRow, cap: number, classId: string, subclassId = ''): void {
            for (const layer of ['spell_known', 'curse_known'] as const) for (const entry of entries(provider, layer)) {
                const id = readLayerListEntryUuid(entry);
                if (!fromBaseUuid(id)) continue;
                const raw = definition(id);
                if (!raw || required(raw) > cap) continue;
                if (known[layer].some(row => readers.itemField(row, 'item_id') === readers.itemField(raw, 'item_id'))) continue;
                known[layer].push({ class_id: [classId], subclass_id: [subclassId], ...fromBaseClone(raw) });
            }
        }
        // Registry classification uses the current contiguous class-slot prelude.
        for (let slot = 1; slot <= 20; slot++) {
            if (!phpJsonTruthy(pc[`class_${slot}`])) break;
            const info = metadata.preliminary.classes.find(row => row.item_id === pc[`class_${slot}`]);
            if (info) layers.bending_caster_type = info.caster_type;
        }
        activeStage = 'player-choices-providers';
        const nested: Record<string, unknown[]> = Object.create(null);
        const planned: FromBaseRow[] = [];
        for (const choice of base.user_choices) {
            if (!fromBaseRecord(choice)) throw new TypeError('Invalid saved user choice');
            if (choice.item_id !== pcId) throw new TypeError('User choice belongs to another PC');
            const rawType = phpJsonString(choice.choice_type ?? ''), layer = rawType.replace(/^user_choice_\d+_/, '');
            if (!/^[a-z_][a-z0-9_]*$/.test(layer)) throw new TypeError(`Invalid choice destination: ${rawType}`);
            const choiceGrant = playerChoiceGrant(choice, base, metadata, definition, value);
            const requiresLevel = choiceGrant.parentRequiresLevel;
            const ability = choiceGrant.ability, source = choice.exact_choice_item_id ?? choice.choice_item_id ?? '';
            if (fromBaseUuid(choice.choice)) {
                const nestedEntry = { source: phpJsonString(source), choice_item_id: phpJsonString(choice.choice_item_id ?? ''),
                    exact_choice_item_id: phpJsonString(choice.exact_choice_item_id ?? ''), requires_level: requiresLevel,
                    built_from: 'user_choices_nested', item_arr: ability, choice_type: rawType };
                for (const parent of new Set([choice.exact_choice_item_id, choice.choice_item_id].filter(fromBaseUuid).map(id => id.toLowerCase()))) {
                    (nested[parent] ??= []).push(fromBaseClone(nestedEntry));
                }
            }
            const additional = choiceGrant.additional;
            if (additional) {
                append(additional.add_ability_name, choice.choice ?? '');
                append(additional.ability_name, choiceGrant.entry);
                continue;
            }
            if (totalLevel < requiresLevel) {
                if (fromBaseUuid(choice.choice)) {
                    const grants = new Map<string, number>();
                    const authored = ability && fromBaseRecord((ability as FromBaseRow).item_layer_types)
                        ? (ability as FromBaseRow).item_layer_types as FromBaseRow : {};
                    for (const [key, raw] of Object.entries(authored)) {
                        if (!/^skill_tier_\d+$/.test(key)) continue;
                        const tier = Number(key.slice('skill_tier_'.length));
                        for (const entry of Array.isArray(raw) ? raw : [raw]) {
                            const id = readLayerListEntryUuid(entry).trim().toUpperCase();
                            if (tier > 0 && fromBaseUuid(id)) grants.set(id, Math.max(tier, grants.get(id) ?? 0));
                        }
                    }
                    planned.push({ item_id: choice.choice, item_name: phpJsonString(nameOf(ability) ?? ''), layer,
                        requires_level: requiresLevel, source: phpJsonString(source),
                        skill_grants: [...grants].map(([s, t]) => ({ s, t })) });
                }
                continue;
            }
            if (choiceGrant.kind === 'none') continue;
            if (choiceGrant.kind === 'defense') {
                const computed = (host.computed ??= {}) as FromBaseRow;
                const creature = (computed.creature ??= {}) as FromBaseRow;
                const defenses = (creature.defenses ??= {}) as FromBaseRow;
                const slot = DEFENSE_SLOTS[layer]!.slot;
                defenses[slot] = foldCreatureDefenseEntries({ family: 'damage',
                    initialEntries: entries(defenses, slot), events: [choiceGrant.defense] });
            } else {
                append(layer, choiceGrant.value);
            }
        }
        host._user_choice_nested_map = nested;
        if (planned.length) {
            const computed = (host.computed ??= {}) as FromBaseRow;
            ((computed.creature ??= {}) as FromBaseRow).planned_user_choices = planned;
        }
        // Selected ancestry rungs are host passives. The existing provider reader
        // suppresses all ancestry provider passives once these three choices resolve.
        if ([1, 2, 3].every(slot => phpJsonTruthy(pc[`ancestry_choice_${slot}`]))) {
            if (![1, 2, 3].every(slot => fromBaseUuid(pc[`ancestry_choice_${slot}`]))) {
                throw new FromBaseStageMissing('ancestry-selection', 'invalid-choice-reference');
            }
            const rungs = new Map<number, Map<number, { id: string; row: ItemFieldRow }>>();
            for (const slot of [1, 2]) {
                const provider = lookup(pc[`ancestry_${slot}`]);
                const map = new Map<number, { id: string; row: ItemFieldRow }>();
                if (provider) for (const entry of entries(provider, 'passive_ability')) {
                    const id = readLayerListEntryUuid(entry), row = lookup(id);
                    const rawRequired = value(row, 'requires_level');
                    if (row && rawRequired != null && [1, 5, 10].includes(phpJsonInt(rawRequired))) map.set(phpJsonInt(rawRequired), { id, row });
                }
                rungs.set(slot, map);
            }
            for (const [choiceIndex, level] of [[1, 1], [2, 5], [3, 10]] as const) {
                if (totalLevel < level && level !== 1) continue;
                const slot = pc[`ancestry_choice_${choiceIndex}`] === pc.ancestry_1 ? 1 : 2;
                const rung = rungs.get(slot)?.get(level);
                if (!rung) {
                    if (level === 1) throw new FromBaseStageMissing('ancestry-selection', `missing-level-1-rung:${slot}`);
                    continue;
                }
                append('passive_ability', { ability_id: rung.id, item_name: nameOf(rung.row), source: pc[`ancestry_${slot}`] ?? '',
                    requires_level: level, item_arr: rung.row, built_from: `ancestry_${slot}_level_${level}`,
                    ...(level === 1 ? { source_type: 'ancestry' } : {}) });
                if (level === 1) {
                    const type = value(rung.row, 'creature_type');
                    if (fromBaseUuid(type)) {
                        host.creature_type = type;
                        layers.creature_type = type;
                        saveSection('creature_type_arr', lookup(type));
                    }
                    const subtypeRefs = [...new Set(entries(rung.row, 'creature_sub_type').map(readLayerListEntryUuid))];
                    if (subtypeRefs.length) {
                        layers.creature_sub_type = subtypeRefs;
                        for (const id of subtypeRefs) saveSection('creature_sub_type_arr', lookup(id));
                    }
                    // Match the native prelude's carrier-aware canonicalization
                    // before restoring this body size after later provider grants.
                    const ancestryHost = { ...host, item_layer_types: { ...layers } };
                    let bodySize = '';
                    for (const entry of entries(ancestryHost, 'size')) bodySize = readItemUuidSlot(entry) || bodySize;
                    if (!bodySize) bodySize = readItemUuidSlot(value(rung.row, 'size'));
                    if (bodySize) {
                        const multiple = metadata.layerCatalog.size?.is_multiple;
                        if (typeof multiple !== 'boolean') throw new FromBaseInputMissing('layer-multiplicity', 'size');
                        layers.size = multiple ? [bodySize] : bodySize;
                    }
                }
            }
        }
        function providerGrants(id: unknown, kind: string, cap: number, classId: string, subclassId = ''): void {
            const provider = lookup(id);
            if (!provider) return;
            saveSection(`${kind}_arr`, provider);
            foldKnown(provider, cap, classId, subclassId);
            for (const abilityType of metadata.abilityTypes) for (const entry of entries(provider, abilityType)) {
                const abilityId = readLayerListEntryUuid(entry), ability = lookup(abilityId);
                if (!ability || required(ability) > cap) continue;
                if (abilityType === 'passive_ability' && (kind === 'class' || kind === 'subclass')) foldKnown(ability, cap, classId, subclassId);
                append(abilityType, { ability_id: abilityId, item_name: nameOf(ability), source: kind === 'background' ? value(host, 'background') : id,
                    requires_level: required(ability), item_arr: ability, built_from: kind });
            }
        }
        providerGrants(pc.lineage, 'lineage', totalLevel, phpJsonString(pc.lineage ?? ''));
        providerGrants(pc.background, 'background', totalLevel, phpJsonString(pc.background ?? ''));
        for (let slot = 1; slot <= 20; slot++) {
            const cap = phpJsonInt(pc[`class_level_${slot}`] ?? 0), classId = phpJsonString(pc[`class_${slot}`] ?? '');
            providerGrants(pc[`class_${slot}`], 'class', cap, classId);
            providerGrants(pc[`subclass_${slot}`], 'subclass', cap, classId, phpJsonString(pc[`subclass_${slot}`] ?? ''));
        }
        if (known.spell_known.length) host.spells_known = known.spell_known;
        if (known.curse_known.length) host.curses_known = known.curse_known;
        const ownGrants = (key: string): unknown[] => {
            const raw = Object.prototype.hasOwnProperty.call(layers, key) ? layers[key] : host[key];
            return raw == null ? [] : Array.isArray(raw) ? raw : [raw];
        };
        // The PC prelude calls override_abilities before preliminary essence. This
        // prefix does not yet own that producer, so it must not price suppressed
        // ancestral passives as though they remained active.
        let hasOverrides = false;
        for (const key of metadata.abilityTypes) for (const entry of ownGrants(key)) {
            const raw = fromBaseRecord(entry) && fromBaseRecord(entry.item_arr) ? entry.item_arr : null;
            if (!raw) continue;
            if (entries(raw, 'override_ability').length) {
                hasOverrides = true;
            }
            if (key === 'feat') for (const grantedType of metadata.abilityTypes) for (const nested of entries(raw, grantedType)) {
                const child = lookup(readLayerListEntryUuid(nested));
                if (child && entries(child, 'override_ability').length) {
                    hasOverrides = true;
                }
            }
        }
        if (hasOverrides) {
            const skillMetadata = metadata as Partial<FromBasePlayerSkillMetadata>;
            if (!skillMetadata.equipmentSlots || !skillMetadata.vitalityStatusIds)
                throw new FromBaseInputMissing('preliminary-condition-metadata', pcId);
            host = createAbilityFinalizerGates({ ...context.dependencies, queryFullItem: definition, defenseIntegerLayers: [],
                equipmentSlots: skillMetadata.equipmentSlots, vitalityStatusIds: skillMetadata.vitalityStatusIds,
                skillEntries: skillMetadata.skills,
                conditionSource: (kind, args) => {
                    try {
                        return answerConditionContext(kind, args, { encounter: base.encounter_context ?? null,
                            campaign: base.encounter_context && fromBaseRecord(base.encounter_context.campaign_arr) ? base.encounter_context.campaign_arr : null,
                            value: readers.layerValue, entries, abilityTypes: metadata.abilityTypes });
                    } catch (error) {
                        if (error instanceof ConditionContextUnknown) throw new FromBaseStageMissing('player-preliminary-ability-override', `${error.kind}:${error.why}`);
                        throw error;
                    }
                } }).override(host) as FromBaseRow;
        }
        // Expose the ordered raw provider view without flattening authored layers.
        const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog,
            nodeItemId: readNodeItemId, resolveCarrierRef: definition });
        host._from_base_providers = [...providers.providers(host)];
        activeStage = 'pc-preliminary-essence';
        // PHP's provider grant loops scrub ability slots from their intermediate
        // provider surfaces. Raw definitions remain immutable; give preliminary
        // readers the equivalent detached view so an EMPTY gated host list cannot
        // fall back to a class's locked passive list. The explicit host grants
        // above retain their complete authored definition and source identity.
        const scrubbedProviderIds = new Set([
            pc.lineage, pc.background,
            ...Array.from({ length: 20 }, (_, i) => pc[`class_${i + 1}`]),
            ...Array.from({ length: 20 }, (_, i) => pc[`subclass_${i + 1}`]),
        ].filter(fromBaseUuid).map(id => id.toUpperCase()));
        const assertPreliminarySources = (raw: ItemFieldRow): void => {
            const authored = fromBaseRecord((raw as FromBaseRow).item_layer_types) ? (raw as FromBaseRow).item_layer_types as FromBaseRow : {};
            for (const [key, fragments] of Object.entries(authored)) {
                if (!/^(?:ancestral_|background_)/.test(key)) continue;
                if (/\[(?:gold_total|(?:half_)?v_max|v_lost|(?:walking|swimming)_speed|speed_[a-z_]+)\]/.test(JSON.stringify(fragments))) {
                    throw new FromBaseStageMissing('preliminary-formula-producer', key);
                }
            }
        };
        const preliminaryDefinition = (id: string): ItemFieldRow | null => {
            const raw = definition(id);
            if (raw) assertPreliminarySources(raw);
            if (!raw || !scrubbedProviderIds.has(id.toUpperCase())) return raw;
            const out = fromBaseClone(raw) as FromBaseRow;
            const authored = out.item_layer_types as FromBaseRow;
            for (const key of metadata.abilityTypes) { delete authored[key]; delete out[key]; }
            return out;
        };
        activeStage = 'classification-template-swarm-media';
        host = constructPlayerClassificationFromBase(base, metadata, host, context);
        activeStage = 'pc-preliminary-essence';
        // Read the same passive closure as the preliminary collector, including
        // default-provider fallback when an incomplete ancestry has no host grant.
        const preliminaryReaders = createFormulaReadContext({ ...context.dependencies, resolveCarrierRef: preliminaryDefinition });
        const preliminaryPassives = Object.values(preliminaryReaders.layerEntries(host, 'passive_ability')).map(entry =>
            fromBaseRecord(entry) && fromBaseRecord(entry.item_arr) ? entry.item_arr : lookup(readLayerListEntryUuid(entry)));
        for (const row of preliminaryPassives) {
            if (!row || !fromBaseRecord((row as FromBaseRow).item_layer_types)) continue;
            const authored = (row as FromBaseRow).item_layer_types as FromBaseRow;
            if (Object.keys(authored).some(key => key.startsWith('ancestral_'))
                && Object.keys(authored).some(key => /^(?:if_|requires_(?!level$|class_level$))/.test(key))) {
                throw new FromBaseStageMissing('preliminary-passive-condition', phpJsonString(readers.itemField(row, 'item_id')));
            }
        }
        // Only actual preliminary source layers are scanned. Later resource,
        // inventory, speed and condition producers may not be replaced by zero.
        for (const raw of [host, ...host._from_base_providers as ItemFieldRow[],
            ...preliminaryPassives]) {
            if (!fromBaseRecord(raw)) continue;
            assertPreliminarySources(raw);
        }
        const row = createPcPreliminaryEssenceCompiler({ ...context.dependencies,
            resolveCarrierRef: preliminaryDefinition,
            queryFullItem: preliminaryDefinition }).pcSequence(host) as FromBaseRow;
        return { kind: 'constructed-prefix', row,
            completedStages: ['player-source-initialization', 'player-choices-providers', 'classification-template-swarm-media', 'pc-preliminary-essence'],
            missingStages: FROM_BASE_PLAYER_REMAINING_STAGES.filter(stage => stage !== 'classification-template-swarm-media') };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input', stage: activeStage, dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage', stage: error.stage, detail: error.detail };
        throw error;
    }
}
